import * as React from "react";
import { cn } from "~/lib/utils";
import { type BottomSheetFolds, type FoldStep, useBottomSheet } from "~/hooks/useBottomSheet";
import { BottomSheetContext } from "./context";

interface BottomSheetRootProps {
  children: React.ReactNode;
  defaultStep?: FoldStep;
  step?: FoldStep;
  onStepChange?: (step: FoldStep) => void;
  className?: string;
  /** Custom heights: { folded: 72, peek: 300, open: 0.85 } */
  folds?: BottomSheetFolds;
  /** Allow closing fully. Default: true */
  dismissible?: boolean;
  /** Overdrag resistance (0-1). Default: 0.3 */
  overdragResistance?: number;
  /** Enable drag to resize. Default: true */
  draggable?: boolean;
}

export function BottomSheetRoot({
  children,
  defaultStep = "peek",
  step: controlledStep,
  onStepChange,
  className,
  folds,
  dismissible,
  overdragResistance,
  draggable = true,
}: BottomSheetRootProps) {
  const sheet = useBottomSheet({
    defaultStep: controlledStep ?? defaultStep,
    onStepChange,
    config: { folds, dismissible, overdragResistance },
  });

  // Sync controlled step
  React.useEffect(() => {
    if (controlledStep !== undefined && controlledStep !== sheet.step) {
      sheet.setStep(controlledStep);
    }
  }, [controlledStep, sheet]);

  const style: React.CSSProperties = {
    height: sheet.height,
  };

  return (
    <BottomSheetContext.Provider
      value={{
        step: sheet.step,
        setStep: sheet.setStep,
        dragging: sheet.dragging,
      }}
    >
      <div
        ref={sheet.sheetRef}
        data-slot="bottom-sheet"
        data-step={sheet.step}
        data-dragging={sheet.dragging || undefined}
        className={cn(
          "flex flex-col overflow-hidden",
          "bg-card rounded-t-xl shadow-lg ring-1 ring-border/50",
          "animate-in slide-in-from-bottom-8 fade-in duration-300 fill-mode-forwards",
          {
            "cursor-grabbing touch-none duration-0 select-none": draggable && sheet.dragging,
            "cursor-grab transition-[height] duration-200 ease-out max-h-[85vh]": draggable && !sheet.dragging,
            "transition-[height] duration-200 ease-out max-h-[85vh]": !draggable,
          },
          className,
        )}
        style={style}
        {...(draggable && sheet.handlers)}
      >
        {children}
      </div>
    </BottomSheetContext.Provider>
  );
}
